import { useState, useRef } from 'react'

import useAuth from '../hooks/useAuth'

import SupportRequestQuestion from './OHAQuestions/SupportRequestQuestion'
import OHSupportingDocuments from './OHAQuestions/OHSupportingDocuments'
import PreviousActionsQuestion from './OHAQuestions/PreviousActionsQuestion'
import ImplementationsQuestion from './OHAQuestions/ImplementationsQuestion'
import DesiredOutcomeQuestion from './OHAQuestions/DesiredOutcomeQuestion'
import EmployeeQuestionsQuestion from './OHAQuestions/EmployeeQuestionsQuestion'
import AdditionalInfoQuestion from './OHAQuestions/AdditionalInfoQuestion'
import DeclarationsQuestion from './OHAQuestions/DeclarationsQuestion'
import IssueNatureQuestion from './OHAQuestions/IssueNatureQuestion'
import OHStaffDetails from './OHAQuestions/OHStaffDetails'

const OHRequestForm = ({ requestData, setRequestData, formData, setFormData, files, setFiles, workingHoursDropdown, setWorkingHoursDropdown, isQuestionComplete }) => {
    const { createInfo } = useAuth()

    const [currentStep, setCurrentStep] = useState(1)
    const totalSteps = 9
    const onLatestStep = currentStep === totalSteps
    const [showValidationErrors, setShowValidationErrors] = useState(false)

    const [questionStates, setQuestionStates] = useState({
        supportRequest: {
            'Policy/Attendance triggers': {
                selected: false,
                subOptions: {
                    'Short term absence': { selected: false },
                    'Long term absence': { selected: false, duration: '' },
                    'Frequent intermittent absence': { selected: false },
                    'Health issue impacting attendance': { selected: false },
                    'Return to work after absence': { selected: false }
                }
            },
            'Conduct and Performance triggers': {
                selected: false,
                subOptions: {
                    'Performance concerns': { selected: false },
                    'Behavioral concerns': { selected: false },
                    'Escalation required by HR or school policy': { selected: false }
                }
            },
            'Health and Safety Triggers': {
                selected: false,
                subOptions: {
                    'Health and safety concern following risk assessment': { selected: false }
                }
            },
            'General Advice': {
                selected: false
            },
            'Would you like to discuss this with our in-house HR specialists?': {
                selected: false
            },
            'Other': {
                selected: false,
                text_response: ''
            }
        },
        issueNature: {
            'Functional impact on role': {
                selected: false,
                subOptions: {
                    'Reduced Functional Capacity': { selected: false },
                    'Struggling To Meet Demands Of Role': { selected: false },
                    'Requires Workplace Adjustments': { selected: false },
                    'Safety Concerns': { selected: false }
                }
            },
            'Health related factors': {
                selected: false,
                subOptions: {
                    'Mental health': {
                        selected: false,
                        subOptions: {
                            'Anxiety/depression': { selected: false },
                            'Stress/burnout': { selected: false },
                            'Other mental health conditions': { selected: false, text_response: '' }
                        }
                    },
                    'Neurodiversity': {
                        selected: false,
                        subOptions: {
                            'ADHD': { selected: false },
                            'Autism': { selected: false },
                            'Other neurodiversity': { selected: false, text_response: '' }
                        }
                    },
                    'Physical health': {
                        selected: false,
                        subOptions: {
                            'Musculoskeletal': { selected: false },
                            'Women\'s health': { selected: false },
                            'Chronic conditions': { selected: false },
                            'Cancer': { selected: false },
                            'Voice related': { selected: false },
                            'Other medical issue': { selected: false, text_response: '' }
                        }
                    }
                }
            },
            'Personal circumstances': {
                selected: false,
                subOptions: {
                    'Caring Responsibilities': { selected: false },
                    'Life Changes – Bereavement, Separation, Health Concerns Etc': { selected: false },
                    'Personal Circumstances Impacting Work': { selected: false }
                }
            },
            'Wellbeing concerns': {
                selected: false,
                subOptions: {
                    'Request For Wellbeing Support': { selected: false }
                }
            },
            'Other factors': {
                selected: false,
                text_response: ''
            }
        },
        previousActions: {
            'Return To Work Meeting': { selected: false },
            'Welfare (Attendance) Meeting': { selected: false },
            'HR Capability Meeting': { selected: false },
            'Informal Check In': { selected: false },
            'No Meetings Yet': { selected: false },
            'Other meetings': { selected: false, text_response: '' },
            'Attendance Plan Agreed': { selected: false },
            'Performance Management Plan Agreed': { selected: false },
            'Adjustments Agreed': { selected: false },
            'Sickness Absence': { selected: false },
            'Escalation In Line With Policy': { selected: false },
            'No Improvement': { selected: false },
            'Other outcomes': { selected: false, text_response: '' }
        },
        implementations: {
            'Temporary Adjustments': { selected: false },
            'Permanent Adjustments': { selected: false },
            'Reduced Hours': { selected: false },
            'Phased Return To Work': { selected: false },
            'Redeployment': { selected: false },
            'Other': { selected: false, text_response: '' },
            'None': { selected: false }
        },
        desiredOutcome: {
            'Advice On Fitness For Work': { selected: false },
            'Advice On Adjustments': { selected: false },
            'Advice On Attendance Management': { selected: false },
            'Advice On Performance Management': { selected: false },
            'Wellbeing Support For Employee': { selected: false },
            'Review Of Workplace Risks': { selected: false },
            'Support With Behavior': { selected: false },
            'Information Only': { selected: false },
            'Unsure': { selected: false }
        },
        employeeQuestions: {
            'Is the employee fit for their normal hours and duties required by their post? If not, when will they be able to return to work/return to normal hours/duties or alternative work?': { selected: false },
            'What if any work adjustments may facilitate a return to the substantive role?': { selected: false },
            'If the substantive role cannot be adjusted sufficiently what temporary alternative duties is the employee fit to undertake safely?': { selected: false },
            'Is there an underlying medical condition affecting their ability to work?': { selected: false },
            'Has the employee accessed appropriate treatment/support to support their health, wellbeing and attendance?': { selected: false },
            'Is there any additional help or treatment recommended to support the employee?': { selected: false },
            'What lifestyle adjustments if any are recommended for the employee to support their ongoing health, wellbeing and work attendance?': { selected: false },
            'Is the employee fit for work in any capacity? If not when is the employee likely to be fit to return to work?': { selected: false },
            'Is this employee likely to be able to provide regular and effective service in the future?': { selected: false },
            'If the employee is unlikely to return to their substantive post for health reasons but can work in an alternative role is permanent redeployment recommended?': { selected: false },
            'If the employee is unlikely to return to their substantive post for health reasons is the employee a candidate for ill health retirement application?': { selected: false },
            'In your view, is the employee likely to be covered under the disability provision of the Equality Act 2010? Occupational health can provide guidance only on this question.': { selected: false },
            'Is the employee fit to attend a formal meeting (i.e. disciplinary)?': { selected: false }
        }
    })

    const [otherText, setOtherText] = useState('')

    const updateQuestionState = (questionKey, newStateOrUpdater) => {
        setQuestionStates(prev => ({
            ...prev,
            [questionKey]: typeof newStateOrUpdater === 'function'
                ? newStateOrUpdater(prev[questionKey])
                : newStateOrUpdater
        }))
    }

    const getValidationErrors = (stepNumber) => {
        const errors = []

        switch (stepNumber) {
            case 1:
                const staffDetails = formData.staff_details
                if (!staffDetails?.first_name?.trim()) errors.push('first_name')
                if (!staffDetails?.last_name?.trim()) errors.push('last_name')
                if (!staffDetails?.staff_email?.trim()) errors.push('staff_email')
                    
                if (!staffDetails?.date_of_birth?.trim()) errors.push('date_of_birth')
                if (!staffDetails?.phone_number?.trim()) errors.push('phone_number')
                if (!staffDetails?.role?.trim()) errors.push('role')
                if (!workingHoursDropdown) errors.push('working_hours_dropdown')
                if (workingHoursDropdown === 'PT' && !staffDetails?.working_hours) errors.push('working_hours')
                if (staffDetails?.in_work === null || staffDetails?.in_work === undefined) errors.push('in_work')
                break
            case 2:
                const q1Responses = formData?.form_responses?.question1?.responses || []
                if (q1Responses.length === 0) errors.push('no_selection')
                break
            case 3:
                const q2Responses = formData?.form_responses?.question2?.responses || []
                if (q2Responses.length === 0) errors.push('no_selection')
                break
            case 4:
                const q3Responses = formData?.form_responses?.question3?.responses || []
                const meetingsParent = q3Responses.find(r => r.answer_text === "Meetings Held")
                const outcomesParent = q3Responses.find(r => r.answer_text === "Outcomes")
                const hasMeetingsHeld = meetingsParent && meetingsParent.sub_responses && meetingsParent.sub_responses.length > 0
                const hasOutcomes = outcomesParent && outcomesParent.sub_responses && outcomesParent.sub_responses.length > 0
                if (!hasMeetingsHeld) errors.push('meetings_held')
                if (!hasOutcomes) errors.push('outcomes')
                break
            case 5:
                const q4Responses = formData?.form_responses?.question4?.responses || []
                if (q4Responses.length === 0) errors.push('no_selection')
                break
            case 6:
                const q5Responses = formData?.form_responses?.question5?.responses || []
                if (q5Responses.length === 0) errors.push('no_selection')
                break
            case 8:
                if (files.fileList.length === 0 && !requestData?.supporting_documents?.length) {
                    errors.push('supporting_documents')
                }
                break
            case 9:
                const decl = formData.declarations
                if (!decl?.purpose_explained) errors.push('purpose_explained')
                if (!decl?.questions_aware) errors.push('questions_aware')
                if (!decl?.abandonment) errors.push('abandonment')
                break
            default:
                break
        }

        return errors
    }

    const isPurposeComplete = () => {
        const decl = formData.declarations
        return decl.purpose_explained && decl.questions_aware && decl.abandonment
    }

    const renderProgressBar = () => (
        <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                    Step {currentStep} of {totalSteps}
                </span>
                <span className="text-sm text-gray-500">
                    {Math.round((currentStep / totalSteps) * 100)}% Complete
                </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
            </div>
            <div className="flex justify-between mt-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
                    <div key={step} className="flex flex-col items-center flex-1 px-1">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-medium ${step <= currentStep
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-500'
                            }`}>
                            {step}
                        </div>
                        <span className={`mt-1 text-xs text-center leading-tight max-w-full break-words ${step <= currentStep ? 'text-blue-600' : 'text-gray-400'
                            }`}>
                            {step === 1 && 'Staff Details'}
                            {step === 2 && 'Support Request'}
                            {step === 3 && 'Issue Nature'}
                            {step === 4 && 'Previous Actions'}
                            {step === 5 && 'Implementations'}
                            {step === 6 && 'Desired Outcome'}
                            {step === 7 && 'Employee Questions'}
                            {step === 8 && 'Documents & Info'}
                            {step === 9 && 'Declarations'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )

    const handleNextStep = () => {
        if (isQuestionComplete(currentStep)) {
            setShowValidationErrors(false)
            setCurrentStep(currentStep + 1)
        } else {
            setShowValidationErrors(true)
            createInfo('error', 'Required Fields Incomplete')
        }
    }

    const renderNavigationButtons = () => (
        <div className="flex justify-between mt-8">
            <button
                onClick={(e) => { e.preventDefault(); setCurrentStep(currentStep - 1); }}
                disabled={currentStep === 1}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${currentStep === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
            >
                Previous
            </button>
            {currentStep !== totalSteps && (
                <button
                    onClick={(e) => { e.preventDefault(); handleNextStep(); }}
                    className="px-6 py-2 rounded-md font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                    Next
                </button>
            )}
        </div>
    )

    return (
        <div className="max-w-4xl mx-auto">
            {renderProgressBar()}

            {currentStep === 1 && (
                <OHStaffDetails
                    requestData={requestData}
                    setRequestData={setRequestData}
                    formData={formData}
                    setFormData={setFormData}
                    workingHoursDropdown={workingHoursDropdown}
                    setWorkingHoursDropdown={setWorkingHoursDropdown}
                    files={files}
                    setFiles={setFiles}
                    validationErrors={showValidationErrors ? getValidationErrors(1) : []}
                />
            )}

            {currentStep === 2 && (
                <SupportRequestQuestion
                    formData={formData}
                    setFormData={setFormData}
                    questionState={questionStates.supportRequest}
                    setQuestionState={(newState) => updateQuestionState('supportRequest', newState)}
                    validationErrors={showValidationErrors ? getValidationErrors(2) : []}
                />
            )}

            {currentStep === 3 && (
                <IssueNatureQuestion
                    formData={formData}
                    setFormData={setFormData}
                    questionState={questionStates.issueNature}
                    setQuestionState={(newState) => updateQuestionState('issueNature', newState)}
                    validationErrors={showValidationErrors ? getValidationErrors(3) : []}
                />
            )}

            {currentStep === 4 && (
                <PreviousActionsQuestion
                    formData={formData}
                    setFormData={setFormData}
                    questionState={questionStates.previousActions}
                    setQuestionState={(newState) => updateQuestionState('previousActions', newState)}
                    validationErrors={showValidationErrors ? getValidationErrors(4) : []}
                />
            )}

            {currentStep === 5 && (
                <ImplementationsQuestion
                    formData={formData}
                    setFormData={setFormData}
                    questionState={questionStates.implementations}
                    setQuestionState={(newState) => updateQuestionState('implementations', newState)}
                    validationErrors={showValidationErrors ? getValidationErrors(5) : []}
                />
            )}

            {currentStep === 6 && (
                <DesiredOutcomeQuestion
                    formData={formData}
                    setFormData={setFormData}
                    questionState={questionStates.desiredOutcome}
                    setQuestionState={(newState) => updateQuestionState('desiredOutcome', newState)}
                    validationErrors={showValidationErrors ? getValidationErrors(6) : []}
                />
            )}

            {currentStep === 7 && (
                <EmployeeQuestionsQuestion
                    formData={formData}
                    setFormData={setFormData}
                    questionState={questionStates.employeeQuestions}
                    setQuestionState={(newState) => updateQuestionState('employeeQuestions', newState)}
                    otherText={otherText}
                    setOtherText={setOtherText}
                />
            )}

            {currentStep === 8 && (
                <div className="space-y-6">
                    <AdditionalInfoQuestion
                        formData={formData}
                        setFormData={setFormData}
                    />

                    <OHSupportingDocuments requestData={requestData} setRequestData={setRequestData} files={files} setFiles={setFiles} validationErrors={showValidationErrors ? getValidationErrors(8) : []} />
                </div>
            )}

            {currentStep === 9 && (
                <DeclarationsQuestion
                    requestData={requestData}
                    setRequestData={setRequestData}
                    formData={formData}
                    setFormData={setFormData}
                    isPurposeComplete={isPurposeComplete}
                    validationErrors={showValidationErrors ? getValidationErrors(9) : []}
                />
            )}

            {renderNavigationButtons()}
        </div>
    )
}

export default OHRequestForm
